<template>
  <div class="map-container">
    <!-- Loading Indicator - Updated Style -->
    <div v-if="loading" class="loading-overlay">
      <div v-if="!getLoaderLogoPath" class="spinner"></div>
      <img v-else :src="getLoaderLogoPath" alt="Loading..." class="loading-logo blinking" />
    </div>
    <div v-else ref="mapContainer" class="google-map"></div>
  </div>
</template>

<script>
import Vue from 'vue'
import maplibregl from 'maplibre-gl'
import VehicleMarker from './VehicleMarker'
import PointMarker from './PointMarker'
import { mapGetters } from 'vuex'

export default {
  props: {
    geofences: {
      type: Array,
      default: () => [],
    },
    vehicleStatuses: {
      type: Array,
      default: () => [],
    },
    showOnlyLast: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    savedZoom: {
      type: Number,
      default: null,
    },
    savedCenter: {
      type: Object,
      default: null,
    },
    polyline: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      map: null,
      region: process.env.VUE_APP_AWS_MAP_REGION,
      mapName: process.env.VUE_APP_AWS_MAP_NAME,
      apiKey: process.env.VUE_APP_AWS_MAP_API_KEY,

      isMapLoaded: false,
      vehicleMarkers: new Map(),
      geofenceMarkers: new Map(),
      polylineLayer: null,
      makassarBounds: [
        [118.9223, -5.6486], // Southwest coordinates
        [119.9891, -4.5736], // Northeast coordinates
      ],
      center: [119.4327, -5.1477], // Makassar, Indonesia center
      currentZoom: 12, // Start with zoom level 12
      isProgrammaticChange: false,
    }
  },
  computed: {
    ...mapGetters({
      getAsset: 'asset/getAsset',
    }),
    getLoaderLogoPath() {
      return this.getAsset('secondaryLogo', 'sidebarMinimize')
    },
    // Normalize vehicle statuses to ensure direction is properly formatted
    normalizedVehicleStatuses() {
      return this.vehicleStatuses.map((vehicleStatus) => ({
        ...vehicleStatus,
        direction: this.normalizeDirection(vehicleStatus.direction),
      }))
    },
    filteredGeofences() {
      if (!this.geofences || this.geofences.length === 0) {
        return []
      }

      if (this.showOnlyLast && this.geofences.length > 2) {
        const firstGeofence = this.geofences[0]
        const lastGeofence = this.geofences[this.geofences.length - 1]

        return [firstGeofence, lastGeofence]
      }

      return this.geofences
    },
  },
  watch: {
    savedZoom: {
      handler(newZoom) {
        if (newZoom !== null && newZoom !== this.currentZoom && this.map) {
          this.isProgrammaticChange = true
          this.currentZoom = newZoom
          this.map.setZoom(newZoom)
          this.$nextTick(() => {
            this.isProgrammaticChange = false
          })
        }
      },
      immediate: true,
    },
    savedCenter: {
      handler(newCenter) {
        if (
          newCenter &&
          this.map &&
          (newCenter.lng !== this.center[0] || newCenter.lat !== this.center[1])
        ) {
          this.isProgrammaticChange = true
          this.center = [newCenter.lng, newCenter.lat]
          this.map.setCenter([newCenter.lng, newCenter.lat])
          this.$nextTick(() => {
            this.isProgrammaticChange = false
          })
        }
      },
      deep: true,
      immediate: true,
    },
    normalizedVehicleStatuses: {
      handler(_) {
        if (this.isMapLoaded) {
          this.updateVehicleMarkers()
        }
      },
      deep: true,
    },
    filteredGeofences: {
      handler(_) {
        if (this.isMapLoaded) {
          this.updateGeofenceMarkers()
        }
      },
      deep: true,
    },
    showOnlyLast: {
      handler() {
        // Auto-fit only if no saved zoom/center from parent
        if (this.savedZoom === null && this.savedCenter === null) {
          this.$nextTick(() => {
            this.fitMapToAllMarkers()
          })
        }
      },
    },
    polyline: {
      handler(_) {
        if (this.isMapLoaded) {
          this.updatePolyline()

          // Auto-fit only if no saved zoom/center
          if (this.savedZoom === null && this.savedCenter === null) {
            this.$nextTick(() => {
              this.fitMapToAllMarkers()
            })
          }
        }
      },
      deep: true,
    },
    loading: {
      handler(isLoading, wasLoading) {
        // When loading finishes, fit the map to all markers
        if (wasLoading && !isLoading && this.isMapLoaded) {
          if (this.savedZoom === null && this.savedCenter === null) {
            setTimeout(() => {
              this.fitMapToAllMarkers()
            }, 600)
          }
        }
      },
    },
  },
  mounted() {
    this.initMap()
    this.setupResponsiveZoom()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    /**
     * Normalize direction to ensure it's a valid number between 0-359
     * @param {number|string} direction - The direction value
     * @returns {number} - Normalized direction
     */
    normalizeDirection(direction) {
      if (direction === null || direction === undefined || direction === '') {
        return 0
      }

      let normalizedDirection = parseFloat(direction)

      if (isNaN(normalizedDirection)) {
        return 0
      }

      // Ensure direction is between 0-359
      normalizedDirection = normalizedDirection % 360
      if (normalizedDirection < 0) {
        normalizedDirection += 360
      }

      return normalizedDirection
    },

    initMap() {
      // Set initial center from savedCenter or try to get user location
      if (this.savedCenter) {
        this.center = [this.savedCenter.lng, this.savedCenter.lat]
      } else {
        this.getUserLocation()
      }

      this.map = new maplibregl.Map({
        container: this.$refs.mapContainer,
        style: `https://maps.geo.${this.region}.amazonaws.com/maps/v0/maps/${this.mapName}/style-descriptor?key=${this.apiKey}`,
        center: this.center,
        zoom: this.currentZoom,
        minZoom: 10,
        maxZoom: 17,
        maxBounds: this.makassarBounds, // hardcoded for damri makassar
        pitchWithRotate: false,
        dragRotate: false,
      })

      this.map.addControl(
        new maplibregl.NavigationControl({
          showCompass: false,
        }),
        'bottom-right'
      )
      this.map.addControl(new maplibregl.FullscreenControl(), 'top-right')

      // Listen to zoom changes
      this.map.on('zoomend', () => {
        if (!this.isProgrammaticChange) {
          this.currentZoom = this.map.getZoom()
          this.$emit('zoom-changed', this.currentZoom)
        }
      })

      // Listen to center changes (only emit when done moving)
      this.map.on('moveend', () => {
        if (!this.isProgrammaticChange) {
          const center = this.map.getCenter()
          this.center = [center.lng, center.lat]
          this.$emit('center-changed', { lat: center.lat, lng: center.lng })
        }
      })

      this.map.on('load', () => {
        this.isMapLoaded = true

        this.updateVehicleMarkers()
        this.updateGeofenceMarkers()
        this.updatePolyline()
      })
    },

    getUserLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          const userLat = position.coords.latitude
          const userLng = position.coords.longitude

          // Check if within Indonesia bounds
          if (userLat >= -11 && userLat <= 6 && userLng >= 95 && userLng <= 141) {
            this.center = [userLng, userLat]

            this.$emit('center-changed', { lat: userLat, lng: userLng })

            if (this.savedZoom === null) {
              this.setDynamicZoom(position.coords.accuracy)
            }
          }
        })
      }
    },

    setDynamicZoom(accuracy) {
      let zoom
      if (accuracy <= 10) {
        zoom = 17
      } else if (accuracy <= 50) {
        zoom = 15
      } else if (accuracy <= 100) {
        zoom = 13
      } else {
        zoom = 10
      }

      this.currentZoom = zoom

      if (this.map) {
        this.map.setZoom(zoom)
      }

      this.$emit('zoom-changed', zoom)
    },

    setupResponsiveZoom() {
      const screenWidth = window.innerWidth

      if (screenWidth <= 576) {
        this.currentZoom = Math.max(this.currentZoom - 1, 3)
      } else if (screenWidth <= 768) {
        this.currentZoom = Math.max(this.currentZoom - 0.5, 3)
      }

      window.addEventListener('resize', this.handleResize)
    },

    handleResize() {
      if (!this.map) return

      const screenWidth = window.innerWidth
      const currentMapZoom = this.map.getZoom()

      if (screenWidth <= 576 && currentMapZoom > 15) {
        this.isProgrammaticChange = true
        this.map.setZoom(15)
        this.$nextTick(() => {
          this.isProgrammaticChange = false
        })
      }
    },

    updateVehicleMarkers() {
      const newIds = new Set()

      // If no vehicle statuses, clear all markers
      if (!this.normalizedVehicleStatuses || this.normalizedVehicleStatuses.length === 0) {
        this.vehicleMarkers.forEach((value) => {
          value.marker.remove()
          value.vm.$destroy()
        })
        this.vehicleMarkers.clear()
        return
      }

      this.normalizedVehicleStatuses.forEach((vehicleStatus, index) => {
        const id = vehicleStatus.vehicle.licensePlate
        newIds.add(id)

        if (!this.vehicleMarkers.has(id)) {
          const MarkerClass = Vue.extend(VehicleMarker)
          const markerInstance = new MarkerClass({
            propsData: {
              id: `vehicle-marker-${vehicleStatus.vehicle.licensePlate || index}`,
              vehicle: vehicleStatus,
            },
          })
          markerInstance.$mount()

          const marker = new maplibregl.Marker({ element: markerInstance.$el })
            .setLngLat([vehicleStatus.longitude, vehicleStatus.latitude])
            .addTo(this.map)

          this.vehicleMarkers.set(id, { marker, vm: markerInstance })
        } else {
          const { marker, vm } = this.vehicleMarkers.get(id)
          marker.setLngLat([vehicleStatus.longitude, vehicleStatus.latitude])
          vm.vehicle = vehicleStatus
        }
      })

      // Remove markers that disappeared
      this.vehicleMarkers.forEach((value, id) => {
        if (!newIds.has(id)) {
          value.marker.remove()
          value.vm.$destroy()
          this.vehicleMarkers.delete(id)
        }
      })
    },

    updateGeofenceMarkers() {
      const newIds = new Set()

      // If no geofences, clear all markers
      if (!this.filteredGeofences || this.filteredGeofences.length === 0) {
        this.geofenceMarkers.forEach((value) => {
          value.marker.remove()
          value.vm.$destroy()
        })
        this.geofenceMarkers.clear()
        return
      }

      this.filteredGeofences.forEach((geofence, index) => {
        const id = geofence.id || `geofence-${index}`
        newIds.add(id)

        const center = this.getGeofenceCenter(geofence)

        if (!this.geofenceMarkers.has(id)) {
          const MarkerClass = Vue.extend(PointMarker)
          const markerInstance = new MarkerClass({
            propsData: {
              id: `geofence-marker-${id}`,
              point: geofence,
              number: index + 1,
              color: this.getMarkerColor(index),
            },
          })
          markerInstance.$mount()

          const marker = new maplibregl.Marker({
            element: markerInstance.$el,
            anchor: 'bottom',
          })
            .setLngLat([center.lng, center.lat])
            .addTo(this.map)

          this.geofenceMarkers.set(id, { marker, vm: markerInstance })
        } else {
          const { marker, vm } = this.geofenceMarkers.get(id)
          marker.setLngLat([center.lng, center.lat])
          vm.point = geofence
          vm.color = this.getMarkerColor(index)
        }
      })

      this.geofenceMarkers.forEach((value, id) => {
        if (!newIds.has(id)) {
          value.marker.remove()
          value.vm.$destroy()
          this.geofenceMarkers.delete(id)
        }
      })
    },

    getGeofenceCenter(geofence) {
      try {
        if (geofence.type === 'C') {
          const match = geofence.coordinates.match(/<\(([^,]+),([^)]+)\),([^>]+)>/)

          if (match) {
            const lat = parseFloat(match[1].trim())
            const lng = parseFloat(match[2].trim())

            if (!isNaN(lat) && !isNaN(lng)) {
              return { lat, lng }
            }
          }
        } else if (geofence.type === 'P') {
          const coordinates = this.parsePolygonCoordinates(geofence.coordinates)
          return this.calculatePolygonCenter(coordinates)
        }
      } catch (error) {
        // Error parsing geofence coordinates, return default
      }

      // Default center (Indonesia)
      return { lat: -2.5489, lng: 118.0149 }
    },

    parsePolygonCoordinates(coordinatesString) {
      const cleanString = coordinatesString.replace(/^\(|\)$/g, '')
      const matches = cleanString.match(/\([^)]+\)/g)

      if (!matches) return []

      return matches.map((match) => {
        const coords = match.replace(/[()]/g, '').split(',')
        return {
          lat: parseFloat(coords[0]),
          lng: parseFloat(coords[1]),
        }
      })
    },

    calculatePolygonCenter(coordinates) {
      if (!coordinates || coordinates.length === 0) {
        return { lat: -2.5489, lng: 118.0149 }
      }

      const bounds = new maplibregl.LngLatBounds()

      coordinates.forEach((coord) => {
        bounds.extend([coord.lng, coord.lat])
      })

      const center = bounds.getCenter()
      return {
        lat: center.lat,
        lng: center.lng,
      }
    },

    getMarkerColor(index) {
      if (index === 0) {
        return '#f41d00'
      } else {
        return '#137c55'
      }
    },

    updatePolyline() {
      if (this.polylineLayer) {
        if (this.map.getLayer('route-line')) {
          this.map.removeLayer('route-line')
        }
        if (this.map.getSource('route')) {
          this.map.removeSource('route')
        }
        this.polylineLayer = null
      }

      // Add new polyline if provided
      if (this.polyline && this.polyline.path && this.polyline.path.length > 0) {
        const coordinates = this.polyline.path.map((point) => [point.lng, point.lat])

        this.map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: coordinates,
            },
          },
        })

        this.map.addLayer({
          id: 'route-line',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': this.polyline.options?.strokeColor || '#0D4491',
            'line-width': this.polyline.options?.strokeWeight || 6,
            'line-opacity': this.polyline.options?.strokeOpacity || 1.0,
          },
        })

        this.polylineLayer = 'route-line'
      }
    },

    fitMapToAllMarkers() {
      if (!this.map) return

      const bounds = new maplibregl.LngLatBounds()
      let hasMarkers = false

      // Add geofence bounds
      if (this.filteredGeofences && this.filteredGeofences.length > 0) {
        this.filteredGeofences.forEach((geofence) => {
          const center = this.getGeofenceCenter(geofence)
          bounds.extend([center.lng, center.lat])
          hasMarkers = true

          // If polygon, add all coordinates
          if (geofence.type === 'P') {
            const coordinates = this.parsePolygonCoordinates(geofence.coordinates)
            coordinates.forEach((coord) => {
              bounds.extend([coord.lng, coord.lat])
            })
          }
        })
      }

      // Add vehicle bounds
      if (this.normalizedVehicleStatuses && this.normalizedVehicleStatuses.length > 0) {
        this.normalizedVehicleStatuses.forEach((vehicleStatus) => {
          if (vehicleStatus.latitude && vehicleStatus.longitude) {
            bounds.extend([vehicleStatus.longitude, vehicleStatus.latitude])
            hasMarkers = true
          }
        })
      }

      // Add polyline bounds
      if (this.polyline && this.polyline.path && this.polyline.path.length > 0) {
        this.polyline.path.forEach((point) => {
          if (point.lat && point.lng) {
            bounds.extend([point.lng, point.lat])
            hasMarkers = true
          }
        })
      }

      if (hasMarkers) {
        this.isProgrammaticChange = true
        this.map.fitBounds(bounds, {
          padding: 100,
          maxZoom: 15,
        })
        setTimeout(() => {
          this.isProgrammaticChange = false
        }, 100)
      }
    },
  },
}
</script>
